import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  AlertCircle,
  CheckCircle,
  ClipboardCopy,
  ClipboardCheck,
  Eye,
  EyeOff,
  Info,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { integrationsApi } from '../../services/integrations-api';
import type {
  ConnectionSetupData,
  ConnectionTestResult,
} from '../../types';

interface MySQLConnectionSetupProps {
  defaultName: string;
  /** Business to file the new source under; backend validates it. */
  organizationId?: string | null;
  onSuccess?: (dataSourceId: string) => void;
  onCancel: () => void;
}

interface MySQLFormState {
  name: string;
  host: string;
  port: string;
  database: string;
  user: string;
  password: string;
  ssl_mode: 'disabled' | 'preferred' | 'required';
  ssl_ca: string;
}

const INITIAL_FORM: Omit<MySQLFormState, 'name'> = {
  host: '',
  port: '3306',
  database: '',
  user: '',
  password: '',
  ssl_mode: 'required',
  ssl_ca: '',
};

  // Replace with the actual Zamino egress IPs once finalized in infra. Surfaced
// to the user so they can allowlist them at their database firewall.
const EGRESS_IPS: string[] = ['<configure in env>'];

/**
 * Extract a connection-failure message that prefers our backend's
 * `{ error, details }` body shape over the generic axios message,
 * so MySQL driver errors surface verbatim to the user.
 */
const extractConnectionError = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | { error?: string; details?: string }
      | undefined;
    return data?.error || data?.details || error.message || fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
};

export const MySQLConnectionSetup: React.FC<MySQLConnectionSetupProps> = ({
  defaultName,
  organizationId,
  onSuccess,
  onCancel,
}) => {
  const [form, setForm] = useState<MySQLFormState>({
    ...INITIAL_FORM,
    name: defaultName,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(
    null,
  );
  const [isTesting, setIsTesting] = useState(false);
  const [grantCopied, setGrantCopied] = useState(false);

  // Build the example GRANT statement dynamically so the user gets a
  // copy-paste-ready snippet that matches what they typed. Uses backticks
  // around the database name (MySQL convention) and a placeholder host.
  const grantStatement = useMemo(() => {
    const db = form.database.trim() || 'your_database';
    const user = form.user.trim() || 'zamino_readonly';
    return [
      `CREATE USER IF NOT EXISTS '${user}'@'%' IDENTIFIED BY '<strong-password-here>';`,
      `GRANT SELECT ON \`${db}\`.* TO '${user}'@'%';`,
      `FLUSH PRIVILEGES;`,
    ].join('\n');
  }, [form.database, form.user]);

  const updateField = <K extends keyof MySQLFormState>(
    key: K,
    value: MySQLFormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (testResult) setTestResult(null);
  };

  const validate = (): boolean => {
    if (!form.name.trim()) {
      toast.error('Connection name is required');
      return false;
    }
    if (!form.host.trim()) {
      toast.error('Host is required');
      return false;
    }
    const portNum = Number(form.port);
    if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535) {
      toast.error('Port must be a number between 1 and 65535');
      return false;
    }
    if (!form.database.trim()) {
      toast.error('Database name is required');
      return false;
    }
    if (!form.user.trim()) {
      toast.error('Username is required');
      return false;
    }
    if (!form.password) {
      toast.error('Password is required');
      return false;
    }
    return true;
  };

  const buildCredentials = () => {
    const ca = form.ssl_ca.trim();
    return {
      host: form.host.trim(),
      port: Number(form.port),
      database: form.database.trim(),
      user: form.user.trim(),
      password: form.password,
      ssl_mode: form.ssl_mode,
      // Only send ssl_ca when the user actually pasted one. Empty string
      // would otherwise round-trip as a meaningless config field.
      ...(ca ? { ssl_ca: ca } : {}),
    };
  };

  const handleTest = async () => {
    if (!validate()) return;
    setIsTesting(true);
    try {
      const result = await integrationsApi.testConnection(
        'mysql',
        buildCredentials(),
      );
      setTestResult(result);
      if (result.success) {
        toast.success('Connection test successful!');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      const message = extractConnectionError(error, 'Connection test failed');
      setTestResult({ success: false, message });
      toast.error(message);
    } finally {
      setIsTesting(false);
    }
  };

  const connectMutation = useMutation({
    mutationFn: (setupData: ConnectionSetupData) =>
      integrationsApi.connectIntegration('mysql', setupData),
    onSuccess: (data) => {
      toast.success('Connected to MySQL — starting schema discovery');
      onSuccess?.(data.dataSource.id);
    },
    onError: (error) => {
      toast.error(extractConnectionError(error, 'Failed to connect MySQL'));
    },
  });

  const handleConnect = () => {
    if (!validate()) return;
    connectMutation.mutate({
      name: form.name.trim(),
      credentials: buildCredentials(),
      testConnection: true,
      ...(organizationId ? { organizationId } : {}),
    });
  };

  const copyGrant = async () => {
    try {
      await navigator.clipboard.writeText(grantStatement);
      setGrantCopied(true);
      toast.success('GRANT statement copied');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  useEffect(() => {
    if (!grantCopied) return;
    const t = setTimeout(() => setGrantCopied(false), 2000);
    return () => clearTimeout(t);
  }, [grantCopied]);

  const isBusy = isTesting || connectMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header info */}
      <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-900 dark:text-blue-200">
            <p className="font-medium mb-1">How federated MySQL works</p>
            <p>
              Your data stays in your database. Zamino queries it on demand
              when you ask a question — read-only, just-in-time, no data
              copied to our servers. Once connected, we'll walk through your
              schema together so future queries are accurate.
            </p>
          </div>
        </div>
      </div>

      {/* Setup checklist */}
      <div className="rounded-lg border border-border bg-muted dark:bg-white/[0.03] p-4 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">
            Before you connect
          </h3>
        </div>

        <div className="space-y-3 text-sm text-foreground">
          <div>
            <p className="font-medium text-foreground">
              1. Server version
            </p>
            <p className="mt-1 text-muted-foreground">
              MySQL 5.7.4+ or 8.0+ required. MariaDB and older MySQL versions
              are not supported in this release.
            </p>
          </div>

          <div>
            <p className="font-medium text-foreground">
              2. Create a read-only user
            </p>
            <p className="mt-1 text-muted-foreground">
              Run this on your MySQL server. The grant restricts Zamino to
              <code className="mx-1 px-1.5 py-0.5 rounded bg-muted text-foreground text-xs">SELECT</code>
              on the database you specify below.
            </p>
            <div className="relative mt-2">
              <pre className="bg-gray-900 text-gray-100 text-xs rounded-md p-3 overflow-x-auto whitespace-pre">
                {grantStatement}
              </pre>
              <button
                type="button"
                onClick={copyGrant}
                className="absolute top-2 right-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-100 transition-colors"
                title="Copy to clipboard"
              >
                {grantCopied ? (
                  <>
                    <ClipboardCheck className="w-3.5 h-3.5" /> Copied
                  </>
                ) : (
                  <>
                    <ClipboardCopy className="w-3.5 h-3.5" /> Copy
                  </>
                )}
              </button>
            </div>
          </div>

          <div>
            <p className="font-medium text-foreground">
              3. Allow Zamino to reach your database
            </p>
            <p className="mt-1 text-muted-foreground">
              If your database is firewalled, allow inbound connections from
              Zamino's egress IP{EGRESS_IPS.length === 1 ? '' : 's'}:
            </p>
            <ul className="mt-1 ml-4 list-disc text-muted-foreground">
              {EGRESS_IPS.map((ip) => (
                <li key={ip}>
                  <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs">
                    {ip}
                  </code>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Connection Name <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="e.g. Production MySQL"
            required
          />
          <p className="text-sm text-muted-foreground">
            A label you'll recognize in the connections list.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Host <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={form.host}
              onChange={(e) => updateField('host', e.target.value)}
              placeholder="db.example.com"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Port <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={form.port}
              onChange={(e) => updateField('port', e.target.value)}
              placeholder="3306"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Database <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            value={form.database}
            onChange={(e) => updateField('database', e.target.value)}
            placeholder="your_database"
            required
          />
          <p className="text-sm text-muted-foreground">
            The database name the read-only user has access to.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Username <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={form.user}
              onChange={(e) => updateField('user', e.target.value)}
              placeholder="zamino_readonly"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => updateField('password', e.target.value)}
                placeholder="••••••••"
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            SSL Mode
          </label>
          <select
            value={form.ssl_mode}
            onChange={(e) =>
              updateField(
                'ssl_mode',
                e.target.value as MySQLFormState['ssl_mode'],
              )
            }
            className="block w-full rounded-lg border border-input px-3 py-2 text-sm transition-colors text-foreground bg-card focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="required" className="bg-card text-foreground">Strict — encrypt + verify certificate</option>
            <option value="preferred" className="bg-card text-foreground">Permissive — encrypt without certificate verification</option>
            <option value="disabled" className="bg-card text-foreground">Disabled — no encryption</option>
          </select>
          <p className="text-sm text-muted-foreground">
            <strong>Strict</strong> verifies the database's certificate against your operating system's
            trust store. Use it when the certificate is signed by a public CA.{' '}
            <strong>Permissive</strong> still encrypts the connection but skips verification — use it for
            managed databases (DigitalOcean, Aiven, AWS RDS without the RDS bundle) when you don't have
            their CA cert handy. Or paste the CA below to keep Strict mode.
          </p>
        </div>

        {/* Optional CA certificate */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            CA Certificate <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <textarea
            value={form.ssl_ca}
            onChange={(e) => updateField('ssl_ca', e.target.value)}
            placeholder="-----BEGIN CERTIFICATE-----&#10;MIID...&#10;-----END CERTIFICATE-----"
            rows={6}
            className="w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary font-mono text-xs"
            spellCheck={false}
          />
          <p className="text-sm text-muted-foreground">
            Paste your provider's CA certificate (PEM) to verify the database's certificate against it.
            <strong> DigitalOcean</strong>: download from your database's "Connection Details" panel under
            "Download CA certificate". <strong>Aiven</strong>: Service Overview → CA Certificate. When pasted,
            this CA is used regardless of SSL Mode (overrides system trust store).
          </p>
        </div>
      </div>

      {/* Test result */}
      {testResult && (
        <div
          className={`border rounded-lg p-4 ${
            testResult.success
              ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
          }`}
        >
          <div className="flex items-start">
            {testResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 mr-3 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
            )}
            <div>
              <h4
                className={`font-medium mb-1 ${
                  testResult.success
                    ? 'text-green-900 dark:text-green-300'
                    : 'text-red-900 dark:text-red-300'
                }`}
              >
                {testResult.success
                  ? 'Connection successful'
                  : 'Connection failed'}
              </h4>
              <p
                className={`text-sm ${
                  testResult.success
                    ? 'text-green-800 dark:text-green-300'
                    : 'text-red-800 dark:text-red-300'
                }`}
              >
                {testResult.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <Button variant="ghost" onClick={onCancel} disabled={isBusy}>
          Cancel
        </Button>
        <Button
          variant="outline"
          onClick={handleTest}
          disabled={isBusy}
        >
          {isTesting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Testing…
            </>
          ) : (
            'Test connection'
          )}
        </Button>
        <Button
          variant="primary"
          onClick={handleConnect}
          disabled={isBusy}
        >
          {connectMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Connecting…
            </>
          ) : (
            'Connect'
          )}
        </Button>
      </div>
    </div>
  );
};

export default MySQLConnectionSetup;
